import React, { useState, useRef, useCallback } from 'react';
import { Layout } from '@/components/layout';
import { fmtDate } from '@/lib/dateFormat';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  Tags, Upload, Play, CheckCircle2, XCircle, Loader2, SkipForward,
  AlertCircle, FileText, Download, RefreshCw, ChevronDown, ChevronUp,
  Clock, History,
} from 'lucide-react';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ──────────────────────────────────────────────────────────────────────

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

type MatchBy = 'handle' | 'sku' | 'id';
type TagMode = 'replace' | 'add' | 'remove' | 'add_remove';

interface TagOperation {
  matchBy: MatchBy;
  matchValue: string;
  mode: TagMode;
  tags?: string[];
  addTags?: string[];
  removeTags?: string[];
}

interface ParsedRow {
  op: TagOperation;
  raw: Record<string, string>;
}

interface TagResult {
  matchValue: string;
  productTitle?: string;
  status: 'updated' | 'skipped' | 'not_found' | 'error';
  oldTags?: string[];
  newTags?: string[];
  error?: string;
}

interface HistoryRun {
  id: string;
  storeName: string;
  filename: string;
  totalCount: number;
  updatedCount: number;
  skippedCount: number;
  notFoundCount: number;
  errorCount: number;
  dryRun: boolean;
  results: TagResult[] | null;
  createdAt: string;
}

// ── CSV Parser — handles multi-line quoted fields (e.g. Shopify Body HTML) ─────

function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }   // escaped ""
        else { inQuotes = false; }                          // closing quote
      } else {
        field += ch;                                        // content incl. newlines
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        record.push(field.trim());
        field = '';
      } else if (ch === '\r') {
        // skip bare CR
      } else if (ch === '\n') {
        record.push(field.trim());
        field = '';
        if (record.some(f => f !== '')) records.push(record);
        record = [];
      } else {
        field += ch;
      }
    }
  }
  // flush last record
  record.push(field.trim());
  if (record.some(f => f !== '')) records.push(record);

  if (records.length < 2) return { headers: [], rows: [] };

  const headers = records[0].map(h => h.toLowerCase().trim());
  const rows = records.slice(1).map(rec => {
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = rec[i] ?? ''; });
    return row;
  });
  return { headers, rows };
}

interface CsvFormat {
  matchBy: MatchBy | null;
  mode: TagMode | null;
  hasTags: boolean;
  hasAddTags: boolean;
  hasRemoveTags: boolean;
  isShopifyExport: boolean;
  valid: boolean;
  error?: string;
}

// Shopify product export CSVs have many columns alongside handle/tags
const SHOPIFY_EXPORT_MARKERS = ['body (html)', 'vendor', 'product category', 'variant sku', 'variant price'];

function detectFormat(headers: string[]): CsvFormat {
  const matchBy: MatchBy | null =
    headers.includes('handle') ? 'handle' :
    headers.includes('sku')    ? 'sku' :
    headers.includes('id')     ? 'id' : null;

  const hasTags       = headers.includes('tags');
  const hasAddTags    = headers.includes('add_tags');
  const hasRemoveTags = headers.includes('remove_tags');

  const isShopifyExport = matchBy === 'handle' && hasTags &&
    SHOPIFY_EXPORT_MARKERS.some(m => headers.includes(m));

  const mode: TagMode | null =
    hasTags                       ? 'replace' :
    hasAddTags && hasRemoveTags   ? 'add_remove' :
    hasAddTags                    ? 'add' :
    hasRemoveTags                 ? 'remove' : null;

  if (!matchBy) return { matchBy, mode, hasTags, hasAddTags, hasRemoveTags, isShopifyExport, valid: false, error: 'CSV must have a "handle", "sku", or "id" column.' };
  if (!mode)    return { matchBy, mode, hasTags, hasAddTags, hasRemoveTags, isShopifyExport, valid: false, error: 'CSV must have a "tags", "add_tags", or "remove_tags" column.' };

  return { matchBy, mode, hasTags, hasAddTags, hasRemoveTags, isShopifyExport, valid: true };
}

function parseTags(val: string): string[] {
  return val.split(',').map(t => t.trim()).filter(Boolean);
}

function rowToOperation(row: Record<string, string>, fmt: CsvFormat): TagOperation {
  return {
    matchBy: fmt.matchBy!,
    matchValue: row[fmt.matchBy!] ?? '',
    mode: fmt.mode!,
    tags:       fmt.hasTags       ? parseTags(row['tags'] ?? '')        : undefined,
    addTags:    fmt.hasAddTags    ? parseTags(row['add_tags'] ?? '')    : undefined,
    removeTags: fmt.hasRemoveTags ? parseTags(row['remove_tags'] ?? '') : undefined,
  };
}

// ── Mode badge label ────────────────────────────────────────────────────────────

const MODE_LABELS: Record<TagMode, string> = {
  replace:    'Replace all',
  add:        'Add tags',
  remove:     'Remove tags',
  add_remove: 'Add & Remove',
};

const MODE_COLORS: Record<TagMode, string> = {
  replace:    'bg-sky-500/15 text-sky-400 border-sky-500/30',
  add:        'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  remove:     'bg-rose-500/15 text-rose-400 border-rose-500/30',
  add_remove: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

// ── Template CSV content ────────────────────────────────────────────────────────

const TEMPLATES: Record<string, { label: string; content: string }> = {
  replace: {
    label: 'Replace tags (handle)',
    content: 'handle,tags\nmy-product,"sale,featured,new-arrival"\nanother-product,"clearance"',
  },
  add: {
    label: 'Add tags (handle)',
    content: 'handle,add_tags\nmy-product,"sale,featured"\nanother-product,"new-arrival"',
  },
  remove: {
    label: 'Remove tags (handle)',
    content: 'handle,remove_tags\nmy-product,"old-tag,deprecated"\nanother-product,"clearance"',
  },
  add_remove: {
    label: 'Add & Remove (sku)',
    content: 'sku,add_tags,remove_tags\nABC123,"sale,featured","old-promo"\nXYZ789,"clearance","featured"',
  },
};

function downloadTemplate(key: string) {
  const tpl = TEMPLATES[key];
  if (!tpl) return;
  const blob = new Blob([tpl.content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tag-update-${key}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ──────────────────────────────────────────────────────────────────

export function TagUpdate() {
  const { token } = useAuthStore();
  const { toast } = useToast();

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [format, setFormat] = useState<CsvFormat | null>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');

  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState<TagResult[] | null>(null);

  const [showTemplates, setShowTemplates] = useState(false);
  const [showFormatGuide, setShowFormatGuide] = useState(false);
  const [expandedResult, setExpandedResult] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<'upload' | 'history'>('upload');
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [expandedRunResult, setExpandedRunResult] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const historyRequestRef = useRef(0);
  const storesRequestRef = useRef(0);

  const fetchHistory = useCallback(async (storeId: string) => {
    if (!storeId || !token) return;
    const requestId = ++historyRequestRef.current;
    setHistoryLoading(true);
    try {
      const r = await fetch(`${BASE}/api/tag-update/stores/${storeId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? 'Failed to load history');
      if (!Array.isArray(data)) throw new Error('Invalid history response');
      if (requestId === historyRequestRef.current) setHistory(data);
    } catch (err: any) {
      if (requestId === historyRequestRef.current) {
        setHistory([]);
        toast({ title: 'Failed to load history', description: err.message, variant: 'destructive' });
      }
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false);
    }
  }, [token]);

  // Load stores on mount
  React.useEffect(() => {
    if (!token) return;
    const requestId = ++storesRequestRef.current;
    fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error ?? 'Failed to load stores');
        if (!Array.isArray(data)) throw new Error('Invalid stores response');
        return data;
      })
      .then(d => {
        if (requestId !== storesRequestRef.current) return;
        const list: ShopifyStore[] = d;
        setStores(list);
        if (list.length === 1) setSelectedStoreId(list[0].id);
      })
      .catch((err: Error) => {
        if (requestId === storesRequestRef.current) toast({ title: 'Failed to load stores', description: err.message, variant: 'destructive' });
      });
  }, [token, toast]);

  // Reload history whenever the store or tab changes
  React.useEffect(() => {
    if (activeTab === 'history' && selectedStoreId) fetchHistory(selectedStoreId);
  }, [activeTab, selectedStoreId, fetchHistory]);

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setParseError('Please upload a .csv file.');
      return;
    }
    setParseError('');
    setResults(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const { headers, rows } = parseCsv(text);
      if (!headers.length) { setParseError('File appears to be empty.'); return; }
      const fmt = detectFormat(headers);
      setFormat(fmt);
      if (!fmt.valid) { setParseError(fmt.error ?? 'Invalid CSV format.'); setParsedRows([]); return; }

      let productRows = rows.filter(r => (r[fmt.matchBy!] ?? '').trim());

      // Shopify export: each product has multiple variant rows with the same handle.
      // Only the first row per handle has the Tags value — deduplicate, keeping first.
      if (fmt.isShopifyExport) {
        const seen = new Set<string>();
        productRows = productRows.filter(r => {
          const key = (r['handle'] ?? '').trim().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      const ops = productRows.map(r => ({ op: rowToOperation(r, fmt), raw: r }));
      if (!ops.length) { setParseError('No valid rows found after the header.'); setParsedRows([]); return; }
      setParsedRows(ops);
    };
    reader.readAsText(file);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const applyUpdates = async (dryRun = false) => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    if (!parsedRows.length) { toast({ title: 'No rows to process', variant: 'destructive' }); return; }
    const storeId = selectedStoreId;
    setApplying(true);
    setResults(null);
    try {
      const res = await fetch(`${BASE}/api/tag-update/stores/${storeId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: parsedRows.map(r => r.op), dryRun, filename: fileName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? 'Failed to apply tag updates');
      const tagResults: TagResult[] = Array.isArray(data.results) ? data.results : [];
      if (storeId !== selectedStoreId) {
        toast({ title: 'Update completed for previous store', description: 'Results were not shown because the selected store changed.' });
        fetchHistory(storeId);
        return;
      }
      setResults(tagResults);
      const updated = tagResults.filter(r => r.status === 'updated').length;
      const skipped = tagResults.filter(r => r.status === 'skipped').length;
      const errors  = tagResults.filter(r => r.status === 'error' || r.status === 'not_found').length;
      toast({
        title: dryRun
          ? `Dry run: ${updated} would update, ${skipped} unchanged, ${errors} issues`
          : `Done — ${updated} updated, ${skipped} unchanged, ${errors} issues`,
      });
      fetchHistory(storeId);
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  const reset = () => {
    setParsedRows([]);
    setFormat(null);
    setFileName('');
    setParseError('');
    setResults(null);
    setExpandedResult(null);
  };

  const updatedCount  = results?.filter(r => r.status === 'updated').length ?? 0;
  const skippedCount  = results?.filter(r => r.status === 'skipped').length ?? 0;
  const notFoundCount = results?.filter(r => r.status === 'not_found').length ?? 0;
  const errorCount    = results?.filter(r => r.status === 'error').length ?? 0;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">

        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-gradient flex items-center gap-3">
            <Tags className="w-7 h-7 text-primary" />
            Bulk Tag Update
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload a CSV to add, remove, or replace product tags across your store in bulk.
          </p>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl border border-border w-fit">
          {([
            { key: 'upload',  label: 'Upload',  Icon: Upload  },
            { key: 'history', label: 'History', Icon: History },
          ] as const).map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === key
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Store selector */}
        <div className="glass-panel rounded-xl p-5 space-y-3">
          <ShopifyTargetStoreSelector
            stores={stores}
            value={selectedStoreId}
            onChange={setSelectedStoreId}
            disabled={applying}
            label="Select Store"
          />
          {stores.length === 0 && (
            <p className="text-xs text-muted-foreground">No stores connected yet — add one in <strong>Connected Stores</strong>.</p>
          )}
        </div>

        {activeTab === 'upload' && <>

        {/* Format guide */}
        <div className="glass-panel rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground hover:text-foreground transition-colors"
            onClick={() => setShowFormatGuide(v => !v)}
          >
            <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> CSV Format Guide</span>
            {showFormatGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showFormatGuide && (
            <div className="px-5 pb-5 space-y-4 border-t border-border/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Match column (required — pick one)</p>
                  <div className="space-y-1 text-sm text-foreground">
                    <p><code className="text-primary">handle</code> — product URL handle</p>
                    <p><code className="text-primary">sku</code> — variant SKU</p>
                    <p><code className="text-primary">id</code> — Shopify product ID</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tag columns (at least one)</p>
                  <div className="space-y-1 text-sm text-foreground">
                    <p><code className="text-sky-400">tags</code> — replaces ALL existing tags</p>
                    <p><code className="text-emerald-400">add_tags</code> — adds to existing tags</p>
                    <p><code className="text-rose-400">remove_tags</code> — removes from existing tags</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Multiple tags within a cell should be comma-separated. Wrap in quotes if needed: <code className="text-foreground">"sale,featured,new"</code></p>

              {/* Template downloads */}
              <div>
                <button
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mb-2"
                  onClick={() => setShowTemplates(v => !v)}
                >
                  <Download className="w-3 h-3" /> Download a template
                  {showTemplates ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showTemplates && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(TEMPLATES).map(([key, tpl]) => (
                      <button
                        key={key}
                        onClick={() => downloadTemplate(key)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1.5"
                      >
                        <Download className="w-3 h-3" /> {tpl.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upload area */}
        {!parsedRows.length && !results && (
          <div
            className={`glass-panel rounded-xl border-2 border-dashed transition-all cursor-pointer ${
              isDragging
                ? 'border-primary/70 bg-primary/5'
                : 'border-border hover:border-border hover:bg-muted/50'
            }`}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center py-14 gap-3 text-center pointer-events-none">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Upload className={`w-5 h-5 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {isDragging ? 'Drop your CSV here' : 'Drag & drop a CSV file here'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFileChange} />
          </div>
        )}

        {/* Parse error */}
        {parseError && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{parseError}</span>
          </div>
        )}

        {/* Preview panel */}
        {parsedRows.length > 0 && !results && (
          <div className="glass-panel rounded-xl p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-medium text-foreground text-sm">{fileName}</span>
                  {format?.isShopifyExport && (
                    <span className="text-xs px-2 py-0.5 rounded border font-medium bg-violet-500/15 text-violet-400 border-violet-500/30">
                      Shopify Export
                    </span>
                  )}
                  {format && (
                    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${MODE_COLORS[format.mode!]}`}>
                      {MODE_LABELS[format.mode!]}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {parsedRows.length} product{parsedRows.length !== 1 ? 's' : ''} ready •{' '}
                  matched by <code className="text-foreground">{format?.matchBy}</code>
                  {format?.isShopifyExport && ' · variant rows skipped'}
                </p>
              </div>
              <button
                onClick={reset}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Change file
              </button>
            </div>

            {/* Table */}
            <div className="rounded-lg border border-border/50 overflow-auto max-h-72">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    <th className="px-3 py-2 text-left text-muted-foreground font-medium">{format?.matchBy}</th>
                    {format?.hasTags      && <th className="px-3 py-2 text-left text-sky-400 font-medium">tags (replace)</th>}
                    {format?.hasAddTags   && <th className="px-3 py-2 text-left text-emerald-400 font-medium">add_tags</th>}
                    {format?.hasRemoveTags && <th className="px-3 py-2 text-left text-rose-400 font-medium">remove_tags</th>}
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((row, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0 hover:bg-muted/50">
                      <td className="px-3 py-2 text-foreground font-mono">{row.op.matchValue || <span className="text-red-400/70 italic">empty</span>}</td>
                      {format?.hasTags && (
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(row.op.tags ?? []).map(t => (
                              <span key={t} className="px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20">{t}</span>
                            ))}
                            {!(row.op.tags ?? []).length && <span className="text-slate-600 italic">empty</span>}
                          </div>
                        </td>
                      )}
                      {format?.hasAddTags && (
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(row.op.addTags ?? []).map(t => (
                              <span key={t} className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+{t}</span>
                            ))}
                          </div>
                        </td>
                      )}
                      {format?.hasRemoveTags && (
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(row.op.removeTags ?? []).map(t => (
                              <span key={t} className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">−{t}</span>
                          ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            {!selectedStoreId && (
              <p className="flex items-center gap-1.5 text-xs text-amber-400/80">
                <AlertCircle className="w-3.5 h-3.5" /> Select a store above before applying.
              </p>
            )}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => applyUpdates(false)}
                disabled={applying || !selectedStoreId}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Apply {parsedRows.length} update{parsedRows.length !== 1 ? 's' : ''}
              </button>
              <button
                onClick={() => applyUpdates(true)}
                disabled={applying || !selectedStoreId}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted/50 hover:bg-muted border border-border text-foreground text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Dry run
              </button>
            </div>
            <p className="text-xs text-slate-600">Dry run previews what would change without modifying Shopify.</p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="glass-panel rounded-xl p-5 space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-semibold text-foreground text-sm">Results</h3>
              <button
                onClick={reset}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Start over
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Updated',   value: updatedCount,  color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                { label: 'Unchanged', value: skippedCount,  color: 'text-muted-foreground',   bg: 'bg-muted/50 border-border' },
                { label: 'Not found', value: notFoundCount, color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20' },
                { label: 'Errors',    value: errorCount,    color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20' },
              ].map(s => (
                <div key={s.label} className={`rounded-lg border p-3 text-center ${s.bg}`}>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Row list */}
            <div className="space-y-1.5 max-h-96 overflow-y-auto custom-scrollbar pr-1">
              {results.map((r, i) => {
                const isExpanded = expandedResult === i;
                const canExpand = r.status === 'updated' || r.status === 'skipped';
                return (
                  <div
                    key={i}
                    className="rounded-lg border border-border/50 bg-muted/30 overflow-hidden"
                  >
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs ${canExpand ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'}`}
                      onClick={() => canExpand && setExpandedResult(isExpanded ? null : i)}
                    >
                      {r.status === 'updated'   && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                      {r.status === 'skipped'   && <SkipForward  className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      {r.status === 'not_found' && <AlertCircle  className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                      {r.status === 'error'     && <XCircle      className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                      <span className="font-mono text-foreground truncate flex-1">{r.matchValue}</span>
                      {r.productTitle && <span className="text-muted-foreground truncate max-w-[180px]">{r.productTitle}</span>}
                      {r.status === 'not_found' && <span className="text-amber-400/80">not found</span>}
                      {r.status === 'error'     && <span className="text-red-400/80 truncate max-w-[200px]">{r.error}</span>}
                      {canExpand && (isExpanded ? <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />)}
                    </button>

                    {isExpanded && canExpand && (
                      <div className="px-3 pb-3 pt-1 border-t border-border/50 grid sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Before</p>
                          <div className="flex flex-wrap gap-1">
                            {(r.oldTags ?? []).length
                              ? (r.oldTags ?? []).map(t => (
                                  <span key={t} className="px-1.5 py-0.5 rounded bg-muted/50 border border-border text-muted-foreground text-[11px]">{t}</span>
                                ))
                              : <span className="text-slate-600 text-[11px] italic">no tags</span>}
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">After</p>
                          <div className="flex flex-wrap gap-1">
                            {(r.newTags ?? []).length
                              ? (r.newTags ?? []).map(t => (
                                  <span key={t} className="px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary text-[11px]">{t}</span>
                                ))
                              : <span className="text-slate-600 text-[11px] italic">no tags</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Upload new file */}
            <button
              onClick={reset}
              className="w-full py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-muted-foreground hover:text-foreground text-sm transition-all flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4" /> Upload another file
            </button>
          </div>
        )}

        </>}

        {/* ── History tab ─────────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Last 50 runs for the selected store</p>
              <button
                onClick={() => selectedStoreId && fetchHistory(selectedStoreId)}
                disabled={historyLoading || !selectedStoreId}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${historyLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>

            {!selectedStoreId && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400">
                <AlertCircle className="w-4 h-4 shrink-0" /> Select a store above to view history.
              </div>
            )}

            {selectedStoreId && historyLoading && (
              <div className="flex items-center justify-center py-12 text-muted-foreground gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
              </div>
            )}

            {selectedStoreId && !historyLoading && history.length === 0 && (
              <div className="glass-panel rounded-xl flex flex-col items-center justify-center py-16 gap-3 text-center">
                <div className="w-10 h-10 rounded-xl bg-muted/50 border border-border flex items-center justify-center">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No runs yet for this store.</p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="text-xs text-primary hover:underline"
                >
                  Upload a CSV to get started
                </button>
              </div>
            )}

            {history.map(run => {
              const isOpen = expandedRunId === run.id;
              const runResults: TagResult[] = Array.isArray(run.results) ? run.results : [];
              const ts = new Date(run.createdAt);
              const dateStr = fmtDate(ts);
              const timeStr = ts.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

              return (
                <div key={run.id} className="glass-panel rounded-xl overflow-hidden">
                  {/* Run header */}
                  <button
                    className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      setExpandedRunId(isOpen ? null : run.id);
                      setExpandedRunResult(null);
                    }}
                  >
                    <div className="flex flex-col items-center gap-0.5 shrink-0 pt-0.5 min-w-[48px]">
                      <span className="text-xs font-semibold text-foreground">{dateStr.split(' ').slice(0,2).join(' ')}</span>
                      <span className="text-[10px] text-muted-foreground">{timeStr}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground truncate max-w-xs">{run.filename}</span>
                        {run.dryRun && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-500/10 text-muted-foreground border-slate-500/20 font-medium">dry run</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {run.updatedCount  > 0 && <span className="text-xs text-emerald-400">{run.updatedCount} updated</span>}
                        {run.skippedCount  > 0 && <span className="text-xs text-muted-foreground">{run.skippedCount} unchanged</span>}
                        {run.notFoundCount > 0 && <span className="text-xs text-amber-400">{run.notFoundCount} not found</span>}
                        {run.errorCount    > 0 && <span className="text-xs text-red-400">{run.errorCount} errors</span>}
                        <span className="text-xs text-slate-600">· {run.totalCount} total</span>
                      </div>
                    </div>

                    <div className="shrink-0 mt-1">
                      {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>

                  {/* Expanded row list */}
                  {isOpen && (
                    <div className="border-t border-border/50 px-5 py-4 space-y-1.5 max-h-80 overflow-y-auto">
                      {runResults.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No detailed results stored.</p>
                      )}
                      {runResults.map((r, i) => {
                        const rowExpanded = expandedRunResult === i;
                        const canExpand = r.status === 'updated' || r.status === 'skipped';
                        return (
                          <div key={i} className="rounded-lg border border-border/50 bg-muted/30 overflow-hidden">
                            <button
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs ${canExpand ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'}`}
                              onClick={() => canExpand && setExpandedRunResult(rowExpanded ? null : i)}
                            >
                              {r.status === 'updated'   && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                              {r.status === 'skipped'   && <SkipForward  className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                              {r.status === 'not_found' && <AlertCircle  className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                              {r.status === 'error'     && <XCircle      className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                              <span className="font-mono text-foreground truncate flex-1">{r.matchValue}</span>
                              {r.productTitle && <span className="text-muted-foreground truncate max-w-[160px]">{r.productTitle}</span>}
                              {r.status === 'not_found' && <span className="text-amber-400/80">not found</span>}
                              {r.status === 'error'     && <span className="text-red-400/80 truncate max-w-[180px]">{r.error}</span>}
                              {canExpand && (rowExpanded ? <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />)}
                            </button>
                            {rowExpanded && canExpand && (
                              <div className="px-3 pb-3 pt-1 border-t border-border/50 grid sm:grid-cols-2 gap-3">
                                <div>
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Before</p>
                                  <div className="flex flex-wrap gap-1">
                                    {(r.oldTags ?? []).length
                                      ? (r.oldTags ?? []).map(t => <span key={t} className="px-1.5 py-0.5 rounded bg-muted/50 border border-border text-muted-foreground text-[11px]">{t}</span>)
                                      : <span className="text-slate-600 text-[11px] italic">no tags</span>}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">After</p>
                                  <div className="flex flex-wrap gap-1">
                                    {(r.newTags ?? []).length
                                      ? (r.newTags ?? []).map(t => <span key={t} className="px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary text-[11px]">{t}</span>)
                                      : <span className="text-slate-600 text-[11px] italic">no tags</span>}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </Layout>
  );
}
